import type { AppDispatch } from '@/app/store';
import {
  createSetupIntent,
  createSubscription,
  resumeSubscriptionPayment,
  setPaymentMethodDefault,
} from '@/features/billing/billingThunks';
import type {
  CreateSubscriptionInput,
  WorkspaceSubscriptionStatus,
} from '@/features/billing/types';
import { shouldReplacePendingPromotion } from '@/lib/checkoutPromoCode';
import type { CheckoutOutcome, ResumeStep } from '@/lib/subscriptionPaymentResume';
import {
  isSamePendingPlan,
  resolveApiErrorCode,
  resolveResumeStep,
  shouldResumePendingPayment,
  SUBSCRIPTION_ERROR_CODES,
  SubscriptionResumeUnavailableError,
  toCheckoutOutcome,
} from '@/lib/subscriptionPaymentResume';

export type { CheckoutOutcome };

/**
 * Le back sérialise la création : le temps qu'elle aboutisse, un second appel repart en conflit.
 * On laisse retomber avant de redemander l'état de reprise, plutôt que de renvoyer une création.
 */
const CONFLICT_RETRY_DELAYS_MS = [700, 1400, 2500];

const wait = (durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs));

/** Seul un 404 dit qu'il n'y a rien à reprendre. Toute autre panne laisse la question ouverte. */
const isNothingToResume = (error: unknown) => (error as { status?: number })?.status === 404;

async function fetchResumeStep(dispatch: AppDispatch, workspaceId: number): Promise<ResumeStep> {
  try {
    return resolveResumeStep(await dispatch(resumeSubscriptionPayment(workspaceId)).unwrap());
  } catch (error) {
    if (isNothingToResume(error)) return { kind: 'create' };
    throw error;
  }
}

/**
 * Point d'entrée unique du tunnel de paiement. Une souscription laissée en attente chez Stripe est
 * reprise, jamais recréée : relancer la création consommerait à nouveau le code promo première
 * transaction déjà porté par sa facture initiale, et Stripe la refuserait. Seule exception : un
 * nouveau code promo, que le back applique en remplaçant la souscription en attente.
 */
export async function ensureSubscriptionPayment({
  dispatch,
  workspaceId,
  input,
  currentStatus,
  pendingPlan,
  requestedPlan,
  pendingPromoCode,
}: {
  dispatch: AppDispatch;
  workspaceId: number;
  input: CreateSubscriptionInput;
  currentStatus: WorkspaceSubscriptionStatus | null | undefined;
  pendingPlan: { planName: string; interval: string } | null | undefined;
  requestedPlan: { nickname: string; interval: string } | null | undefined;
  /** Code porté par la souscription en attente, celui que le back a enregistré sur le workspace. */
  pendingPromoCode?: string | null;
}): Promise<CheckoutOutcome> {
  const replacesPendingPromotion = shouldReplacePendingPromotion({
    currentStatus,
    requestedPromoCode: input.promoCode,
    pendingPromoCode,
  });
  if (
    !replacesPendingPromotion &&
    shouldResumePendingPayment(currentStatus) &&
    isSamePendingPlan(pendingPlan, requestedPlan)
  ) {
    // Une panne de l'API de reprise remonte telle quelle : on n'enchaîne pas sur une création
    // alors qu'une souscription attend son paiement.
    const step = await fetchResumeStep(dispatch, workspaceId);
    if (step.kind === 'unusable') throw new SubscriptionResumeUnavailableError();
    const outcome = toCheckoutOutcome(step);
    if (outcome) return outcome;
  }

  try {
    const created = await dispatch(createSubscription({ workspaceId, input })).unwrap();
    return created.paymentIntentClientSecret
      ? {
          kind: 'confirm',
          subscriptionId: created.subscriptionId,
          clientSecret: created.paymentIntentClientSecret,
        }
      : { kind: 'settled' };
  } catch (error) {
    if (resolveApiErrorCode(error) !== SUBSCRIPTION_ERROR_CODES.requestInProgress) {
      throw error;
    }
    for (const delayMs of CONFLICT_RETRY_DELAYS_MS) {
      await wait(delayMs);
      try {
        const outcome = toCheckoutOutcome(await fetchResumeStep(dispatch, workspaceId));
        if (outcome) return outcome;
      } catch {
        // La création concurrente n'a pas encore abouti : on retente, sans jamais re-poster.
      }
    }
    throw error;
  }
}

/** Réponse de Stripe à la confirmation d'une carte, réduite à ce dont le tunnel a besoin. */
export type CardSetupResult = {
  error: { message?: string } | null;
  paymentMethodId: string | null;
};

export type SavedCard =
  | { kind: 'saved'; paymentMethodId: string }
  | { kind: 'refused'; message: string | null }
  | { kind: 'missing' };

export function readPaymentMethodId(
  paymentMethod: string | { id?: string } | null | undefined,
): string | null {
  if (typeof paymentMethod === 'string') return paymentMethod;
  return paymentMethod?.id ?? null;
}

/**
 * Enregistre la carte saisie et la passe par défaut sans attendre le webhook : sans carte par
 * défaut, Stripe n'a rien à prélever et l'impayé n'apparaît qu'au renouvellement.
 */
export async function saveDefaultCard({
  dispatch,
  workspaceId,
  confirmCardSetup,
}: {
  dispatch: AppDispatch;
  workspaceId: number;
  confirmCardSetup: (clientSecret: string) => Promise<CardSetupResult>;
}): Promise<SavedCard> {
  const { clientSecret } = await dispatch(createSetupIntent(workspaceId)).unwrap();
  const setup = await confirmCardSetup(clientSecret);
  if (setup.error) return { kind: 'refused', message: setup.error.message ?? null };
  if (!setup.paymentMethodId) return { kind: 'missing' };
  await dispatch(
    setPaymentMethodDefault({ workspaceId, paymentMethodId: setup.paymentMethodId }),
  ).unwrap();
  return { kind: 'saved', paymentMethodId: setup.paymentMethodId };
}

export type NewCardCheckout =
  | { kind: 'cardNotSaved'; card: Exclude<SavedCard, { kind: 'saved' }> }
  | { kind: 'ready'; paymentMethodId: string; outcome: CheckoutOutcome };

/**
 * La carte passe avant la souscription : créée d'abord, une souscription avec essai ouvrait
 * l'accès au produit alors que la banque refusait ensuite la carte.
 * Une carte déjà enregistrée lors d'un essai précédent est réutilisée : la réenregistrer
 * l'attacherait une seconde fois au client Stripe.
 */
export async function checkoutWithNewCard({
  confirmCardSetup,
  savedPaymentMethodId,
  onCardSaved,
  ...checkout
}: Parameters<typeof ensureSubscriptionPayment>[0] & {
  confirmCardSetup: (clientSecret: string) => Promise<CardSetupResult>;
  savedPaymentMethodId?: string | null;
  onCardSaved?: (paymentMethodId: string) => void;
}): Promise<NewCardCheckout> {
  let paymentMethodId = savedPaymentMethodId;
  if (!paymentMethodId) {
    const card = await saveDefaultCard({
      dispatch: checkout.dispatch,
      workspaceId: checkout.workspaceId,
      confirmCardSetup,
    });
    if (card.kind !== 'saved') return { kind: 'cardNotSaved', card };
    paymentMethodId = card.paymentMethodId;
    onCardSaved?.(paymentMethodId);
  }
  return {
    kind: 'ready',
    paymentMethodId,
    outcome: await ensureSubscriptionPayment(checkout),
  };
}
